import React, { useEffect, useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TemplateFormData, CredentialField } from '../TemplateWizard';

interface CredentialSchemaStepProps {
  data: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  onValidate: (isValid: boolean) => void;
}

const fieldTypes = [
  { value: 'string', label: 'Texto' },
  { value: 'password', label: 'Senha' },
  { value: 'url', label: 'URL' },
  { value: 'number', label: 'Número' },
  { value: 'boolean', label: 'Booleano' },
] as const;

const emptyField: CredentialField = {
  name: '',
  type: 'string',
  label: '',
  required: true,
  placeholder: '',
  description: '',
  validation: {},
};

export const CredentialSchemaStep: React.FC<CredentialSchemaStepProps> = ({
  data,
  onChange,
  onValidate,
}) => {
  const [expandedField, setExpandedField] = useState<number | null>(null);
  const fields = data.credentialSchema;

  // Add new field
  const addField = () => {
    onChange({
      credentialSchema: [...fields, { ...emptyField }],
    });
    setExpandedField(fields.length);
  };

  // Remove field
  const removeField = (index: number) => {
    onChange({
      credentialSchema: fields.filter((_, i) => i !== index),
    });
    if (expandedField === index) {
      setExpandedField(null);
    }
  };

  // Update field
  const updateField = (index: number, updates: Partial<CredentialField>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange({ credentialSchema: newFields });
  };

  // Move field up/down
  const moveField = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const newFields = [...fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    onChange({ credentialSchema: newFields });
    setExpandedField(newIndex);
  };

  // Validate on data change
  useEffect(() => {
    // At least one field is recommended but not required
    const isValid = fields.every(
      (field) => field.name.trim() && field.label.trim()
    );
    onValidate(isValid || fields.length === 0);
  }, [fields, onValidate]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Schema de Credenciais</h2>
        <p className="text-sm text-surface-400">
          Defina os campos que o usuário precisará preencher ao configurar uma instância deste provider
        </p>
      </div>

      {/* Fields List */}
      <div className="space-y-3">
        {fields.map((field, index) => (
          <div
            key={index}
            className="border border-surface-600 rounded-lg bg-surface-800/50 overflow-hidden"
          >
            {/* Field Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-700/50"
              onClick={() => setExpandedField(expandedField === index ? null : index)}
            >
              <GripVertical className="w-4 h-4 text-surface-500" />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">
                    {field.label || 'Campo sem nome'}
                  </span>
                  {field.required && (
                    <span className="text-xs text-red-400">*</span>
                  )}
                  <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                    {fieldTypes.find(t => t.value === field.type)?.label || field.type}
                  </span>
                </div>
                {field.name && (
                  <p className="text-xs text-surface-500 truncate">
                    Campo: {field.name}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveField(index, 'up'); }}
                  disabled={index === 0}
                  className="p-1 text-surface-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); moveField(index, 'down'); }}
                  disabled={index === fields.length - 1}
                  className="p-1 text-surface-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeField(index); }}
                  className="p-1 text-surface-400 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Field Details (Expanded) */}
            {expandedField === index && (
              <div className="px-4 pb-4 pt-2 border-t border-surface-700 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Field Name */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-surface-300">
                      Nome do Campo <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateField(index, { name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                      placeholder="api_key"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                    <p className="text-xs text-surface-500">Identificador único (sem espaços)</p>
                  </div>

                  {/* Label */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-surface-300">
                      Label <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="API Key"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Type */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-surface-300">
                      Tipo
                    </label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, { type: e.target.value as any })}
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Required */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-surface-300">
                      Obrigatório
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, { required: e.target.checked })}
                        className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-surface-300">Campo obrigatório</span>
                    </label>
                  </div>
                </div>

                {/* Placeholder */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-surface-300">
                    Placeholder
                  </label>
                  <input
                    type="text"
                    value={field.placeholder || ''}
                    onChange={(e) => updateField(index, { placeholder: e.target.value })}
                    placeholder="Texto de exemplo..."
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-surface-300">
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={field.description || ''}
                    onChange={(e) => updateField(index, { description: e.target.value })}
                    placeholder="Instruções para o usuário..."
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                {/* Validation (for string/password types) */}
                {(field.type === 'string' || field.type === 'password') && (
                  <div className="space-y-3 p-3 bg-surface-900 rounded-lg">
                    <p className="text-xs font-medium text-surface-300">Validação</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="block text-xs text-surface-400">Min. caracteres</label>
                        <input
                          type="number"
                          value={field.validation?.minLength || ''}
                          onChange={(e) => updateField(index, { 
                            validation: { 
                              ...field.validation, 
                              minLength: e.target.value ? parseInt(e.target.value) : undefined 
                            } 
                          })}
                          min={0}
                          className="w-full px-2 py-1.5 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs text-surface-400">Max. caracteres</label>
                        <input
                          type="number"
                          value={field.validation?.maxLength || ''}
                          onChange={(e) => updateField(index, { 
                            validation: { 
                              ...field.validation, 
                              maxLength: e.target.value ? parseInt(e.target.value) : undefined 
                            } 
                          })}
                          min={0}
                          className="w-full px-2 py-1.5 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs text-surface-400">Regex (padrão)</label>
                      <input
                        type="text"
                        value={field.validation?.pattern || ''}
                        onChange={(e) => updateField(index, { 
                          validation: { 
                            ...field.validation, 
                            pattern: e.target.value || undefined 
                          } 
                        })}
                        placeholder="^[a-zA-Z0-9]+$"
                        className="w-full px-2 py-1.5 bg-surface-800 border border-surface-600 rounded text-white text-xs font-mono focus:outline-none focus:border-primary-500"
                      />
                    </div>

                    {field.validation?.pattern && (
                      <div className="space-y-1">
                        <label className="block text-xs text-surface-400">Mensagem de erro</label>
                        <input
                          type="text"
                          value={field.validation?.patternMessage || ''}
                          onChange={(e) => updateField(index, { 
                            validation: { 
                              ...field.validation, 
                              patternMessage: e.target.value || undefined 
                            } 
                          })}
                          placeholder="Formato inválido"
                          className="w-full px-2 py-1.5 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Empty State */}
        {fields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-surface-600 rounded-lg">
            <p className="text-surface-400 mb-2">Nenhum campo definido</p>
            <p className="text-xs text-surface-500">
              Adicione campos que o usuário precisará preencher ao configurar este provider
            </p>
          </div>
        )}
      </div>

      {/* Add Field Button */}
      <button
        type="button"
        onClick={addField}
        className={cn(
          'flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-dashed transition-colors',
          'border-surface-600 text-surface-400 hover:border-primary-500 hover:text-primary-400'
        )}
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">Adicionar Campo</span>
      </button>

      {/* Common Templates */}
      <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
        <p className="text-xs font-medium text-surface-300 mb-3">Templates Comuns</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({
              credentialSchema: [
                { name: 'api_key', type: 'password', label: 'API Key', required: true, placeholder: 'Sua API Key' },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            API Key Simples
          </button>
          <button
            type="button"
            onClick={() => onChange({
              credentialSchema: [
                { name: 'username', type: 'string', label: 'Username', required: true },
                { name: 'password', type: 'password', label: 'Password', required: true },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            Username + Password
          </button>
          <button
            type="button"
            onClick={() => onChange({
              credentialSchema: [
                { name: 'client_id', type: 'string', label: 'Client ID', required: true },
                { name: 'client_secret', type: 'password', label: 'Client Secret', required: true },
              ],
            })}
            className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
          >
            OAuth2 Credentials
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredentialSchemaStep;
